import React from "react";
import {
  FiUsers as Users,
  FiTrendingUp as TrendingUp,
  FiCheckCircle as CheckCircle,
  FiXCircle as XCircle,
} from "react-icons/fi";
import type { IconType } from "react-icons";

type Props = {
  totalJoiners: number;
  selectedCount: number;
};

type ColorKey = "blue" | "emerald" | "amber";

type StatItem = {
  label: string;
  value: number;
  icon: IconType;
  color: ColorKey;
};

const COLOR_TEXT_GRADIENT: Record<ColorKey, string> = {
  blue: "from-blue-500 to-blue-600 text-white",
  emerald: "from-emerald-500 to-emerald-600 text-white",
  amber: "from-amber-500 to-amber-600 text-white",
} as const;

const COLOR_BG_BORDER: Record<ColorKey, string> = {
  blue: "bg-blue-50 border-blue-200",
  emerald: "bg-emerald-50 border-emerald-200",
  amber: "bg-amber-50 border-amber-200",
} as const;

const getColorClasses = (color: ColorKey): string => COLOR_TEXT_GRADIENT[color];
const getBgClasses = (color: ColorKey): string => COLOR_BG_BORDER[color];

export const QuickStats: React.FC<Props> = ({ selectedCount, totalJoiners }) => {
  const stats: StatItem[] = [
    {
      label: "Total Joiners",
      value: totalJoiners,
      icon: Users,
      color: "blue",
    },
    {
      label: "Selected Joiners",
      value: selectedCount,
      icon: CheckCircle,
      color: "emerald",
    },
    {
      label: "Unselected Joiners",
      value: Math.max(0, totalJoiners - selectedCount),
      icon: XCircle,
      color: "amber",
    },
  ];

  return (
    <section className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-blue-600" />
          Quick Stats
        </h3>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {stats.map((stat, index) => (
          <div
            key={stat.label}
            className={`relative p-6 rounded-2xl border-2 transition-all duration-500 hover:scale-105 hover:shadow-xl group ${getBgClasses(
              stat.color
            )}`}
            style={{ animationDelay: `${index * 100}ms` }}
          >
            <div className="flex items-center justify-between mb-4">
              <div className={`p-3 rounded-xl bg-gradient-to-r ${getColorClasses(stat.color)} shadow-lg`}>
                <stat.icon className="w-6 h-6" />
              </div>
            </div>

            <div>
              <p className="text-sm font-medium text-gray-600 mb-1">{stat.label}</p>
              <p className="text-3xl font-bold text-gray-900">{stat.value}</p>
            </div>

            <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-white/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          </div>
        ))}
      </div>
    </section>
  );
};
